import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid,
  TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ApartmentIcon from '@mui/icons-material/Apartment';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import AcessoNegado from '../components/AcessoNegado';

const STATUS = {
  disponivel: { label: 'Disponível', cor: '#16A34A' },
  em_campo:   { label: 'Em campo',   cor: '#2563EB' },
  em_reuniao: { label: 'Em reunião', cor: '#D97706' },
  ferias:     { label: 'Férias',     cor: '#7C3AED' },
  afastado:   { label: 'Afastado',   cor: '#94A3B8' },
};

const PERFIL = {
  diretor:    { label: 'Diretor',    bg: '#d4a574', fg: '#1a1a2e' },
  gestor:     { label: 'Gestor',     bg: '#13375F', fg: '#ffffff' },
  consultor:  { label: 'Consultor',  bg: '#e94560', fg: '#ffffff' },
  financeiro: { label: 'Financeiro', bg: '#16A34A', fg: '#ffffff' },
  comercial:  { label: 'Comercial',  bg: '#2563EB', fg: '#ffffff' },
  marketing:  { label: 'Marketing',  bg: '#D97706', fg: '#ffffff' },
  rh:         { label: 'RH',         bg: '#7C3AED', fg: '#ffffff' },
};

const COR_LINHA = '#3b4663';
const ACCENT = '#e94560';

function CartaoPessoa({ pessoa, destaque, onClick }) {
  const st = STATUS[pessoa.status] || STATUS.disponivel;
  const pf = PERFIL[pessoa.perfil] || { label: pessoa.perfil };
  const rotulo = (pessoa.cargo || pf.label || '').toUpperCase();

  return (
    <Box
      onClick={onClick}
      sx={{
        width: 236,
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.75,
        borderRadius: '14px',
        cursor: 'pointer',
        textAlign: 'left',
        background: 'rgba(255,255,255,0.05)',
        border: destaque ? `1px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.09)',
        boxShadow: destaque
          ? `0 0 0 2px rgba(233,69,96,0.18), 0 16px 36px rgba(0,0,0,0.45)`
          : '0 10px 24px rgba(0,0,0,0.35)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { transform: 'translateY(-3px)' },
      }}
    >
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          backgroundColor: ACCENT,
          backgroundImage: pessoa.foto_url ? `url(${pessoa.foto_url})` : 'none',
          backgroundSize: 'cover', backgroundPosition: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 17, fontWeight: 700,
        }}
      >
        {!pessoa.foto_url && pessoa.nome_completo?.charAt(0)}
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        <Typography
          sx={{
            fontSize: 13.5, fontWeight: 700, color: '#f3f5fb',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160,
          }}
        >
          {pessoa.nome_completo}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: st.cor, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 10, color: '#8b96b8' }}>{st.label}</Typography>
        </Box>
        <Box
          sx={{
            display: 'inline-block', alignSelf: 'flex-start', px: 0.9, py: 0.2,
            borderRadius: '999px', background: 'rgba(255,255,255,0.07)',
            border: `1px solid ${ACCENT}`, color: destaque ? '#ffb3c0' : ACCENT,
            fontSize: 9, fontWeight: 700, letterSpacing: '0.02em',
            fontFamily: 'monospace', maxWidth: 160,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {rotulo}
        </Box>
      </Box>
    </Box>
  );
}

function RamoOrganograma({ pessoas, filhosPorPai, nivel, aoClicar, visitados }) {
  const mostrarConector = nivel > 0 && pessoas.length > 1;

  return (
    <Box
      component="ul"
      sx={{
        display: 'flex', justifyContent: 'center', listStyle: 'none', m: 0, p: 0,
        position: 'relative',
        ...(nivel > 0 && {
          pt: '32px',
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: '50%',
            width: 0, height: '32px', borderLeft: `2px solid ${COR_LINHA}`,
          },
        }),
      }}
    >
      {pessoas.map((pessoa, idx) => {
        const jaVisitado = visitados.has(pessoa.id);
        const filhos = jaVisitado ? [] : (filhosPorPai[pessoa.id] || []);
        const novosVisitados = jaVisitado ? visitados : new Set(visitados).add(pessoa.id);

        return (
          <Box
            component="li"
            key={pessoa.id}
            sx={{
              listStyle: 'none', position: 'relative', px: 2, textAlign: 'center',
              pt: mostrarConector ? '32px' : 0,
              ...(mostrarConector && {
                '&::before, &::after': {
                  content: '""', position: 'absolute', top: 0, right: '50%',
                  width: '50%', height: '32px', borderTop: `2px solid ${COR_LINHA}`,
                },
                '&::after': { right: 'auto', left: '50%', borderLeft: `2px solid ${COR_LINHA}` },
                ...(idx === 0 && { '&::before': { border: 'none' } }),
                ...(idx === pessoas.length - 1 && {
                  '&::after': { border: 'none' },
                  '&::before': { borderRight: `2px solid ${COR_LINHA}` },
                }),
              }),
            }}
          >
            <CartaoPessoa
              pessoa={pessoa}
              destaque={nivel === 0}
              onClick={() => aoClicar(pessoa.id)}
            />
            {filhos.length > 0 && (
              <RamoOrganograma
                pessoas={filhos}
                filhosPorPai={filhosPorPai}
                nivel={nivel + 1}
                aoClicar={aoClicar}
                visitados={novosVisitados}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function MembrosEquipe() {
  const { temAcesso } = useAuth();
  const acessoLiberado = temAcesso('membros_equipe');
  const [membros, setMembros] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!acessoLiberado) { setLoading(false); return; }
    api.get('/membros-equipe/').then((res) => {
      setMembros(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [acessoLiberado]);

  if (!acessoLiberado) {
    return (
      <AcessoNegado mensagem="Você não tem acesso à lista de membros da equipe. Fale com o Admin se precisar desse acesso." />
    );
  }

  const buscando = busca.trim().length > 0;
  const filtrados = membros.filter((c) =>
    c.nome_completo?.toLowerCase().includes(busca.toLowerCase()) ||
    c.cargo?.toLowerCase().includes(busca.toLowerCase())
  );

  const porId = {};
  membros.forEach((m) => { porId[m.id] = m; });

  const filhosPorPai = {};
  const raizes = [];
  membros.forEach((m) => {
    if (m.reporta_para && porId[m.reporta_para]) {
      if (!filhosPorPai[m.reporta_para]) filhosPorPai[m.reporta_para] = [];
      filhosPorPai[m.reporta_para].push(m);
    } else {
      raizes.push(m);
    }
  });

  return (
    <Box>
      <PageHeader titulo="Equipe" />
      <Typography variant="body2" color="text.secondary" mb={3}>
        Todos os membros ativos da equipe
      </Typography>

      <TextField
        fullWidth
        placeholder="Buscar por nome ou cargo..."
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        sx={{ mb: 3 }}
        slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) } }}
      />

      {loading ? (
        <Carregando />
      ) : buscando ? (
        filtrados.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">Nenhum membro da equipe encontrado.</Typography>
          </CardContent></Card>
        ) : (
          <Grid container spacing={2}>
            {filtrados.map((c) => {
              const st = STATUS[c.status] || STATUS.disponivel;
              const pf = PERFIL[c.perfil] || { label: c.perfil, bg: '#1a1a2e', fg: '#fff' };
              const rotuloCracha = c.cargo || pf.label;
              return (
                <Grid item xs={12} sm={6} md={4} key={c.id}>
                  <Card
                    sx={{
                      height: '100%', cursor: 'pointer', borderRadius: 3,
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' },
                    }}
                    onClick={() => navigate(`/membros-equipe/${c.id}`)}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 56, height: 56, borderRadius: '50%', p: '2.5px', flexShrink: 0,
                            border: `2px solid ${st.cor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%', height: '100%', borderRadius: '50%',
                              backgroundColor: '#e94560',
                              backgroundImage: c.foto_url ? `url(${c.foto_url})` : 'none',
                              backgroundSize: 'cover', backgroundPosition: 'center',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'white', fontSize: 20, fontWeight: 700,
                            }}
                          >
                            {!c.foto_url && c.nome_completo?.charAt(0)}
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={700} noWrap>
                            {c.nome_completo}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.8 }}>
                        <Box sx={{ backgroundColor: '#13375F', color: '#ffffff', px: 1, py: 0.3, borderRadius: 1, fontSize: 11, fontWeight: 700 }}>
                          {rotuloCracha.toUpperCase()}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: st.cor }} />
                          <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{st.label}</Typography>
                        </Box>
                      </Box>

                      {c.celula?.nome && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 1.3, pt: 1.3, borderTop: '1px solid #f0f0f0', color: '#94a3b8' }}>
                          <ApartmentIcon sx={{ fontSize: 13 }} />
                          <Typography sx={{ fontSize: 12 }}>{c.celula.nome}</Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )
      ) : membros.length === 0 ? (
        <Card><CardContent>
          <Typography color="text.secondary" textAlign="center">Nenhum membro da equipe encontrado.</Typography>
        </CardContent></Card>
      ) : (
        <Box
          sx={{
            position: 'relative',
            borderRadius: '18px',
            overflowX: 'auto',
            background: 'radial-gradient(circle at 50% -10%, #1f2a4d 0%, #141b30 45%, #0d1120 100%)',
            p: { xs: 3, md: 5 },
            '&::before': {
              content: '""',
              position: 'absolute', inset: 0,
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2, mb: 4 }}>
            <Box sx={{ width: 4, height: 26, borderRadius: '2px', backgroundColor: ACCENT }} />
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#f3f5fb' }}>Organograma</Typography>
          </Box>
          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', minWidth: 'fit-content' }}>
            <RamoOrganograma
              pessoas={raizes}
              filhosPorPai={filhosPorPai}
              nivel={0}
              aoClicar={(id) => navigate(`/membros-equipe/${id}`)}
              visitados={new Set()}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}